import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { SpeakerHigh, SpeakerSlash, SquaresFour } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Mascot } from "@/components/styleguide/Mascot";
import { useNarration } from "../year1/addition/useNarration";
import { sfx, unlockAudio, setSoundMuted } from "../year1/addition/sound";
import { Sky } from "./Sky";
import { Meadow } from "./Meadow";
import { Scenery } from "./Scenery";
import { Ambient } from "./Ambient";
import { CountingTree, StoryBook, Postbox, MyPlaceDen, AdventureSign } from "./Landmarks";
import { LivingElements } from "./LivingElements";
import { WorldPanels } from "./WorldPanels";
import { getWorld, getTimeOfDay, getChildForYear, setActiveChild } from "./worldStore";
import { YEAR_CONFIG, STAGES, getTodaysAdventure } from "./yearConfig";
import { ChildAvatar } from "../select-child/ChildAvatar";

const MASCOT_LINES = [
  ["Hello!", "I'm so happy you're here."],
  ["Your world grows", "every time you learn!"],
  ["You are doing", "brilliantly!"],
];

// Visual maturity dial — same places, same navigation, older presentation.
const SIZE = {
  early: { tree: "h-32 sm:h-44 lg:h-52", book: "h-20 sm:h-28 lg:h-32", sign: "h-28 sm:h-36 lg:h-40", post: "h-24 sm:h-28", den: "h-20 sm:h-28", mascotSm: 86, mascotLg: 116 },
  middle: { tree: "h-28 sm:h-40 lg:h-44", book: "h-[4.5rem] sm:h-24 lg:h-28", sign: "h-24 sm:h-32 lg:h-36", post: "h-20 sm:h-24", den: "h-[4.5rem] sm:h-24", mascotSm: 72, mascotLg: 96 },
  upper: { tree: "h-24 sm:h-36 lg:h-40", book: "h-16 sm:h-20 lg:h-24", sign: "h-24 sm:h-28 lg:h-32", post: "h-[4.25rem] sm:h-20", den: "h-16 sm:h-20", mascotSm: 60, mascotLg: 80 },
};

function Place({ testid, label, ariaLabel, className, onTap, glow, bob = true, reduce, children }) {
  return (
    <motion.button
      data-testid={testid}
      aria-label={ariaLabel || label}
      onClick={onTap}
      className={cn("group absolute z-20 flex flex-col items-center focus-visible:outline-none", className)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.94 }}
    >
      {glow && !reduce && (
        <motion.span
          aria-hidden
          className="absolute -inset-4 rounded-full bg-secondary/50 blur-xl"
          animate={{ opacity: [0.35, 0.8, 0.35], scale: [0.95, 1.08, 0.95] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      <motion.span
        className="relative block"
        animate={bob && !reduce ? { y: [0, -5, 0] } : {}}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
      >
        {children}
      </motion.span>
      <span
        className={cn(
          "relative mt-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-extrabold shadow-soft backdrop-blur transition-colors sm:text-xs",
          glow ? "bg-secondary text-secondary-foreground" : "bg-card/90 text-foreground group-hover:bg-card",
        )}
      >
        {label}
      </span>
    </motion.button>
  );
}

export default function HomeWorld() {
  const { yearId } = useParams();
  const match = /^year([1-6])$/.exec(yearId || "");
  if (!match) return <Navigate to="/select-child" replace />;
  const year = Number(match[1]);
  return <World key={year} year={year} cfg={YEAR_CONFIG[year]} />;
}

function World({ year, cfg }) {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const { speak, cancel } = useNarration();

  const band = cfg.band;
  const early = band === "early";
  const sz = SIZE[band];

  const child = useMemo(() => getChildForYear(year), [year]);
  const [{ period, greeting }] = useState(getTimeOfDay);
  const [world] = useState(() => getWorld(child.id));
  const [stage, setStage] = useState(early ? "splash" : "live");
  const [panel, setPanel] = useState(null);
  const [muted, setMuted] = useState(false);
  const [caption, setCaption] = useState("");
  const [mascotBounce, setMascotBounce] = useState(0);

  const mutedRef = useRef(false);
  const timers = useRef([]);
  const lineIdx = useRef(0);
  const adventure = useRef(getTodaysAdventure(year));

  useEffect(() => { setActiveChild(child.id); }, [child.id]);

  const after = useCallback((ms, fn) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
  }, []);

  useEffect(() => {
    mutedRef.current = muted;
    setSoundMuted(muted);
    if (muted) cancel();
  }, [muted, cancel]);

  useEffect(() => () => { timers.current.forEach(clearTimeout); cancel(); }, [cancel]);

  const say = useCallback((lines) => {
    setCaption(lines.join(" "));
    if (!mutedRef.current) speak(lines);
  }, [speak]);

  const awake = stage !== "splash";
  const live = stage === "live";

  const wake = useCallback(() => {
    unlockAudio();
    if (!mutedRef.current) sfx.tap();
    setStage("sunrise");
    after(reduce ? 250 : 2000, () => {
      setStage("greet");
      if (!mutedRef.current) sfx.pop();
      say([`${greeting}, ${child.name}!`, "Welcome to your world."]);
      after(reduce ? 800 : 4000, () => {
        setStage("live");
        say(["Ready for today's adventure?", "Tap the golden sign!"]);
      });
    });
  }, [after, reduce, greeting, child.name, say]);

  const openPlace = useCallback((id, lines) => {
    if (!live) return;
    unlockAudio();
    if (!mutedRef.current) sfx.tap();
    say(lines);
    setPanel(id);
  }, [live, say]);

  const goAdventure = useCallback(() => {
    if (!live) return;
    unlockAudio();
    if (!mutedRef.current) sfx.success();
    const adv = adventure.current;
    if (adv.to) {
      say(["Today's adventure!", `Let's play ${adv.title}!`]);
      after(reduce ? 400 : 1700, () => navigate(adv.to));
    } else {
      say(["Today's adventure!", `${adv.title}.`]);
      setPanel("adventure");
    }
  }, [live, say, after, reduce, navigate]);

  const tapMascot = useCallback(() => {
    if (!awake) return;
    unlockAudio();
    if (!mutedRef.current) sfx.pop();
    setMascotBounce((b) => b + 1);
    say(MASCOT_LINES[lineIdx.current % MASCOT_LINES.length]);
    lineIdx.current += 1;
  }, [awake, say]);

  const tapElement = useCallback((el) => {
    unlockAudio();
    if (!mutedRef.current) sfx.pop();
    say([el.label]);
  }, [say]);

  const tapTopic = useCallback((topic) => {
    unlockAudio();
    if (!mutedRef.current) sfx.tap();
    say([`${topic} is growing here.`, "Coming soon!"]);
  }, [say]);

  const closePanel = useCallback(() => setPanel(null), []);
  const playGame = useCallback((g) => {
    if (!mutedRef.current) sfx.tap();
    navigate(g.to);
  }, [navigate]);

  return (
    <div data-testid="home-world" data-year={year} className="relative h-[100svh] w-full overflow-hidden bg-[#AEE3F7]">
      <Sky period={period} awake={awake} reduce={reduce} />
      <Meadow period={period} palette={cfg.ground} />

      {/* TOP BAR — identical navigation chrome for every year */}
      <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-4 py-2.5 sm:px-6">
        <span className="flex items-center gap-2 rounded-full bg-card/85 py-1.5 pl-2 pr-3.5 shadow-soft backdrop-blur">
          <Mascot size={24} animated={false} />
          <span className="font-display text-base font-black tracking-tight text-foreground">Bloom</span>
        </span>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            aria-label="Open the Bloom design docs"
            data-testid="world-docs-link"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-card/70 text-muted-foreground shadow-soft backdrop-blur transition hover:bg-card hover:text-foreground"
          >
            <SquaresFour size={19} weight="bold" />
          </Link>
          <button
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? "Turn sound on" : "Turn sound off"}
            data-testid="world-mute"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-card/85 text-foreground shadow-soft backdrop-blur transition hover:bg-card"
          >
            {muted ? <SpeakerSlash size={20} weight="bold" /> : <SpeakerHigh size={20} weight="fill" />}
          </button>
        </div>
      </div>

      {/* CHILD IDENTITY — greeting chip (tap to switch explorer) */}
      {awake && (
        <motion.button
          data-testid="greeting-chip"
          onClick={() => navigate("/select-child")}
          aria-label="Switch explorer"
          initial={{ opacity: 0, x: -14 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: reduce ? 0 : 0.5 }}
          className="absolute left-4 top-[3.9rem] z-30 flex items-center gap-2.5 rounded-2xl bg-card/90 p-1.5 pr-4 text-left shadow-soft backdrop-blur transition hover:bg-card sm:left-6"
        >
          <ChildAvatar variant={child.avatar} className="h-10 w-10 sm:h-12 sm:w-12" />
          <span className="leading-tight">
            <span className="block font-display text-sm font-black text-foreground sm:text-base">{child.name}</span>
            <span className="block text-[10px] font-extrabold text-primary-strong sm:text-[11px]">Year {year} {STAGES[year]}</span>
            <span className="block text-[10px] font-bold text-muted-foreground sm:text-[11px]">{greeting}!</span>
          </span>
        </motion.button>
      )}

      {/* BLOOM SPEAKS — narration bubble */}
      <div className="pointer-events-none absolute inset-x-0 top-[8.4rem] z-30 flex min-h-[44px] items-start justify-center px-4 sm:top-16">
        <AnimatePresence mode="wait">
          {awake && caption && (
            <motion.div
              key={caption}
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex max-w-[92%] items-center gap-2 rounded-full bg-card px-4 py-2 shadow-soft sm:max-w-[55%]"
            >
              <SpeakerHigh size={16} weight="fill" className="flex-shrink-0 text-primary" />
              <span data-testid="world-caption" className="text-sm font-bold text-foreground sm:text-base">{caption}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* THE WORLD — same permanent places every year; visuals mature via config */}
      <AnimatePresence>
        {awake && (
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: reduce ? 0 : early ? 1.2 : 0.7, delay: reduce ? 0 : early ? 0.8 : 0.1 }}
          >
            <Scenery year={year} />
            <Ambient reduce={reduce} />

            {/* Maths — the Counting Tree */}
            <Place
              testid="place-maths"
              label={early ? `${cfg.fruit.join(" ")} · Maths` : "Maths"}
              ariaLabel="Maths — the Counting Tree"
              reduce={reduce}
              onTap={() => openPlace("maths", ["Numbers!", "Look what grows on the Counting Tree."])}
              className="left-[1%] bottom-[45%] sm:left-[7%] sm:bottom-[25%] lg:left-[10%]"
            >
              <CountingTree fruit={cfg.fruit} className={cn("w-auto drop-shadow-md", sz.tree)} />
            </Place>

            {/* English — the Story Book */}
            <Place
              testid="place-english"
              label={early ? `${cfg.glyphs.join(" ")} · English` : "English"}
              ariaLabel="English — the Story Book"
              reduce={reduce}
              onTap={() => openPlace("english", ["Letters and stories", "live in the Story Book!"])}
              className="right-[2%] bottom-[46%] sm:right-[6%] sm:bottom-[27%] lg:right-[9%]"
            >
              <StoryBook glyphs={cfg.glyphs} className={cn("w-auto drop-shadow-md", sz.book)} />
            </Place>

            {/* Today's Adventure — the golden signpost */}
            <Place
              testid="place-adventure"
              label="Today's Adventure"
              reduce={reduce}
              glow={live}
              bob={false}
              onTap={goAdventure}
              className="left-1/2 bottom-[29%] -translate-x-1/2 sm:left-[52%] sm:bottom-[24%] sm:translate-x-0"
            >
              <AdventureSign className={cn("w-auto drop-shadow-lg", sz.sign)} />
            </Place>

            {/* Bloom — the companion */}
            <motion.button
              data-testid="mascot-companion"
              aria-label="Bloom, your companion — tap to say hello"
              onClick={tapMascot}
              className="absolute left-[7%] bottom-[21%] z-20 origin-bottom focus-visible:outline-none sm:left-[33%] sm:bottom-[22%] lg:left-[36%]"
              initial={{ scale: 0, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 15, delay: reduce ? 0 : 0.4 }}
              whileTap={{ scale: 0.92 }}
            >
              <motion.span
                key={mascotBounce}
                className="block"
                animate={reduce ? {} : mascotBounce ? { y: [0, -14, 0, -6, 0] } : { y: [0, -6, 0] }}
                transition={mascotBounce && !reduce ? { duration: 0.8 } : { duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              >
                <Mascot size={sz.mascotSm} animated={false} mood={stage === "greet" ? "celebrating" : "happy"} className="drop-shadow-md sm:hidden" />
                <Mascot size={sz.mascotLg} animated={false} mood={stage === "greet" ? "celebrating" : "happy"} className="hidden drop-shadow-md sm:block" />
              </motion.span>
            </motion.button>

            {/* My Place — the child's own den */}
            <Place
              testid="place-my-place"
              label="My Place"
              reduce={reduce}
              bob={false}
              onTap={() => openPlace("my-place", ["This is your very own place!"])}
              className="left-[3%] bottom-[3%] sm:left-[8%] sm:bottom-[4%] lg:left-[13%]"
            >
              <MyPlaceDen band={band} className={cn("w-auto drop-shadow-md", sz.den)} />
            </Place>

            {/* Homework — the Postbox */}
            <Place
              testid="place-homework"
              label="Homework"
              ariaLabel="Homework — the Postbox"
              reduce={reduce}
              onTap={() => openPlace("homework", ["You have a letter", "in the postbox!"])}
              className="right-[4%] bottom-[3%] sm:right-[12%] sm:bottom-[5%] lg:right-[16%]"
            >
              <Postbox extraMail={!early} className={cn("w-auto drop-shadow-md", sz.post)} />
            </Place>

            {/* Everything this child has grown so far */}
            <LivingElements elements={world.elements} onTap={tapElement} reduce={reduce} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* PANELS */}
      <WorldPanels
        panel={panel}
        onClose={closePanel}
        child={child}
        year={year}
        cfg={cfg}
        elementsCount={world.elements.length}
        adventure={adventure.current}
        onPlay={playGame}
        onTopicTap={tapTopic}
      />

      {/* SLEEPING WORLD — the wake-up ritual belongs to the youngest explorers */}
      <AnimatePresence>
        {stage === "splash" && (
          <motion.button
            data-testid="wake-button"
            onClick={wake}
            aria-label="Tap to wake up your world"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: reduce ? 0 : 1.8, ease: "easeInOut" } }}
            className="absolute inset-0 z-50 block w-full cursor-pointer bg-gradient-to-b from-[#1B2040] via-[#2A3060] to-[#4A4380] focus-visible:outline-none"
          >
            {[["10%", "12%"], ["24%", "28%"], ["38%", "8%"], ["55%", "20%"], ["70%", "10%"], ["84%", "26%"], ["48%", "34%"], ["16%", "42%"]].map(([left, top], i) => (
              <motion.span
                key={i}
                aria-hidden
                className="absolute h-1.5 w-1.5 rounded-full bg-white"
                style={{ left, top }}
                animate={reduce ? {} : { opacity: [0.25, 1, 0.25] }}
                transition={{ duration: 2.2, delay: i * 0.35, repeat: Infinity }}
              />
            ))}
            <span aria-hidden className="absolute right-[14%] top-[10%] block h-14 w-14 rounded-full bg-[#FFF3D6] shadow-[0_0_40px_rgba(255,243,214,0.45)] sm:h-20 sm:w-20">
              <span className="absolute right-2 top-1.5 block h-11 w-11 rounded-full bg-[#2A3060] sm:h-16 sm:w-16" />
            </span>
            <span aria-hidden className="absolute -left-[24%] bottom-0 block h-[30%] w-[90%] rounded-[50%] bg-[#151A33]" />
            <span aria-hidden className="absolute -right-[20%] bottom-[-8%] block h-[32%] w-[95%] rounded-[50%] bg-[#10142A]" />

            <span className="absolute inset-0 flex flex-col items-center justify-center px-6">
              <span className="relative block">
                <Mascot size={110} animated={false} className="opacity-80 saturate-[0.7]" />
                {!reduce &&
                  ["z", "z", "z"].map((z, i) => (
                    <motion.span
                      key={i}
                      aria-hidden
                      className="absolute font-display font-black text-white/70"
                      style={{ right: -10 - i * 14, top: 8 - i * 16, fontSize: 14 + i * 5 }}
                      animate={{ opacity: [0, 1, 0], y: [4, -6] }}
                      transition={{ duration: 2.4, delay: i * 0.55, repeat: Infinity }}
                    >
                      {z}
                    </motion.span>
                  ))}
              </span>
              <span className="mt-4 block font-display text-4xl font-black tracking-tight text-white sm:text-5xl">Bloom</span>
              <span className="mt-1.5 block text-sm font-bold text-white/70">{child.name}'s world is sleeping…</span>
              <motion.span
                className="mt-8 inline-flex min-h-[60px] items-center rounded-full bg-secondary px-8 font-display text-lg font-black text-secondary-foreground shadow-[0_0_36px_rgba(255,209,102,0.5)]"
                animate={reduce ? {} : { scale: [1, 1.06, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              >
                Tap to wake up your world
              </motion.span>
            </span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
